import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Edit, Trash2, Upload } from "lucide-react";

export default function AdminDialogs({
  selectedDoc,
  setSelectedDoc,
  editMode,
  setEditMode,
  editData,
  setEditData,
  handleEditDoc,
  handleSaveDoc,
  handleDeleteDoc,
  createCoachMode,
  setCreateCoachMode,
  newCoachData,
  setNewCoachData,
  handleCreateCoach,
  loading,
  importMode,
  setImportMode,
  selectedCoachId,
  setSelectedCoachId,
  coachList,
  handleBulkUpload,
}) {
  return (
    <>
      <Dialog open={!!selectedDoc} onOpenChange={() => { setSelectedDoc(null); setEditMode(false); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Document: {selectedDoc?.id}</span>
              <div className="flex gap-2">
                {!editMode ? (
                  <Button size="sm" variant="outline" onClick={handleEditDoc}>
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handleSaveDoc}>
                    Save Changes
                  </Button>
                )}
                <Button size="sm" variant="destructive" onClick={() => handleDeleteDoc(selectedDoc?.id)}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {editMode ? (
              <Textarea
                value={editData}
                onChange={(e) => setEditData(e.target.value)}
                rows={20}
                className="font-mono text-sm"
              />
            ) : (
              <pre className="bg-slate-50 p-4 rounded-lg text-sm overflow-x-auto border">
                {JSON.stringify(selectedDoc, null, 2)}
              </pre>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={createCoachMode} onOpenChange={setCreateCoachMode}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Coach Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCoach} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="coach-name">Full Name</Label>
              <Input
                id="coach-name"
                value={newCoachData.name}
                onChange={(e) => setNewCoachData({ ...newCoachData, name: e.target.value })}
                placeholder="e.g. John Doe"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="coach-email">Email Address</Label>
              <Input
                id="coach-email"
                type="email"
                value={newCoachData.email}
                onChange={(e) => setNewCoachData({ ...newCoachData, email: e.target.value })}
                placeholder="coach@example.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="coach-password">Password</Label>
              <Input
                id="coach-password"
                type="password"
                value={newCoachData.password}
                onChange={(e) => setNewCoachData({ ...newCoachData, password: e.target.value })}
                placeholder="Secure password"
                required
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setCreateCoachMode(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={loading}>
                {loading ? "Creating..." : "Create Coach"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={importMode} onOpenChange={setImportMode}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Import Students</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Select Coach</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white"
                value={selectedCoachId}
                onChange={(e) => setSelectedCoachId(e.target.value)}
              >
                <option value="">-- Select a Coach --</option>
                {coachList.map((coach) => (
                  <option key={coach.id} value={coach.id}>
                    {coach.name} ({coach.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Upload CSV File</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:bg-slate-50 transition-colors">
                <input
                  type="file"
                  id="csv-upload-admin"
                  accept=".csv"
                  className="hidden"
                  onChange={handleBulkUpload}
                  disabled={!selectedCoachId}
                />
                <Button
                  variant="outline"
                  onClick={() => document.getElementById("csv-upload-admin").click()}
                  disabled={!selectedCoachId}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Select CSV File
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  Format: Name, DOB, Parent Name, Parent Email
                </p>
              </div>
            </div>

            <div className="bg-amber-50 p-3 rounded text-xs text-amber-700">
              Note: Parent accounts will be auto-created if they don't exist.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
